import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { cleanTitle } from '../../../server/services/utils.service';
import type { ThreadOrTaskMode } from '../../../server/types/message-types';
import { threadTitleMaxLength } from '../../../server/validation/message-validation';
import { MessageService, ThreadItem } from '../message.service';

export type ThreadEditTitleData = {
  authUserId: string;
  orgId: string;
  threadId: string;
  mode: ThreadOrTaskMode;
};

@Component({
  selector: 'app-thread-edit-title-dialog',
  templateUrl: './thread-edit-title-dialog.component.html',
  styleUrls: ['./thread-edit-title-dialog.component.scss'],
  standalone: false,
})
export class ThreadEditTitleDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<ThreadEditTitleDialogComponent>);
  private readonly data: ThreadEditTitleData = inject(MAT_DIALOG_DATA);

  private snackBar = inject(MatSnackBar);
  private messageService = inject(MessageService);

  authUserId: string;
  orgId: string;
  threadId: string;
  mode: ThreadOrTaskMode;

  currentTitle: string;
  proposedTitle: string;

  threadItem: ThreadItem;

  form = new FormGroup({
    title: new FormControl('', {
      validators: [
        Validators.required,
        Validators.maxLength(threadTitleMaxLength),
      ],
      nonNullable: true,
    }),
  });

  subscriptions = new Subscription();

  constructor() {
    this.authUserId = this.data.authUserId;
    this.orgId = this.data.orgId;
    this.threadId = this.data.threadId;
    this.mode = this.data.mode;
  }

  ngOnInit(): void {
    const titleChangeSubscription =
      this.form.controls.title.valueChanges.subscribe((value: string) => {
        if (typeof value === 'undefined') return;
        const newValue = cleanTitle(value);
        // Only run the change if it needs to be changed
        if (value !== newValue) this.form.controls.title.setValue(newValue);
      });
    this.subscriptions.add(titleChangeSubscription);

    const { orgId, threadId } = this;
    this.threadItem = this.messageService.getThreadItem({
      orgId,
      refId: threadId,
      mode: this.mode,
    });

    // Set the initial value
    const titleSub = this.threadItem.titleReplayObservable
      .pipe(take(1))
      .subscribe((title) => {
        this.form.controls.title.setValue(title);
      });
    this.subscriptions.add(titleSub);
  }

  sendUpdateTitle(): void {
    const uncleanTitle = this.form.controls.title.value;
    const title = cleanTitle(uncleanTitle);
    // Only send update if it is actually making a change
    if (title === this.threadItem.title) return this.closeDialog();
    const updateObservable = this.threadItem.updateTitle(title);
    // Wait until the message is accepted before closing the dialog
    const subscription = updateObservable.pipe(take(1)).subscribe({
      next: (message) => {
        this.closeDialog();
      },
      error: (err) => {
        this.snackBar.open('ERROR: could not update title.', 'Dismiss', {
          duration: 5000,
        });
        this.closeDialog();
      },
    });
    this.subscriptions.add(subscription);
  }

  closeDialog(): void {
    this.dialogRef.close('Edit title dialog closed');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
